import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Session } from 'next-auth';
import {
  Subscriptions,
  NewsletterQuestion,
  NewsletterSessionManager,
} from 'lib/services/newsletter-signup-service';
import useDictionary from 'lib/hooks/useDictionary';
import { isUnselectedQuestionValue } from 'lib/utils/question-options';
import { useSessionSafe } from './auth/useSessionSafe';

// Dictionary keys for Profile/Briteverify rule-based errors — mirrors registration-service mapping.
const PROFILE_ERROR_DICTIONARY_KEYS: Record<string, string> = {
  EMAIL_FORMAT: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailFormat',
  EMAIL_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailProfanity',
  FIRST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameChar',
  FIRST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.FirstNameProfanity',
  LAST_NAME_CHAR: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameChar',
  LAST_NAME_PROFANITY: 'Common.LoginAndRegistration.EmailRegistrationResponses.LastNameProfanity',
  EMAIL_DISPOSABLE: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDisposable',
  EMAIL_ACCOUNT_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAccountInvalid',
  EMAIL_ADDRESS_INVALID:
    'Common.LoginAndRegistration.EmailRegistrationResponses.EmailAddressInvalid',
  EMAIL_DOMAIN_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailDomainInvalid',
  EMAIL_ROLE_INVALID: 'Common.LoginAndRegistration.EmailRegistrationResponses.EmailRoleInvalid',
  PROFILE_FAIL: 'Common.LoginAndRegistration.EmailRegistrationResponses.ProfileFail',
};

export interface NewsletterSignupResult {
  success: boolean;
  error?: string;
  errorDescription?: string;
}

export interface UseNewsletterSignupReturn {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  submitNewsletter: (
    email?: string,
    subscriptions?: Subscriptions[],
    questions?: NewsletterQuestion[]
  ) => Promise<void>;
  submitNewsletterForLoggedInUser: (
    subscriptions?: Subscriptions[],
    questions?: NewsletterQuestion[]
  ) => Promise<void>;
  reset: () => void;
  userEmail: string | null;
  shouldShowComponent: boolean;
  isCheckingSubscription: boolean;
  isLoggedIn: boolean;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  email: string;
  submittedEmail: string;
  emailError: string | null;
  businessIndustry: string;
  businessIndustryError: string | null;
  handleBusinessIndustryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export interface UseNewsletterSignupOptions {
  businessIndustryRequired?: boolean;
  businessIndustryErrorMessage?: string;
  businessIndustryQuestionId?: string;
  defaultBusinessIndustryValue?: string;
  onSuccess?: (subscriptions?: Subscriptions[]) => void;
}

/**
 * Custom hook for newsletter signup functionality
 * Handles API integration for newsletter subscription
 * Supports both anonymous and authenticated user workflows
 * Manages component visibility based on subscription status
 */
export const useNewsletterSignup = (
  session?: Session | null,
  subscriptions?: Subscriptions[],
  emailErrorMessage?: string,
  options: UseNewsletterSignupOptions = {}
): UseNewsletterSignupReturn => {
  const {
    businessIndustryRequired,
    businessIndustryErrorMessage,
    businessIndustryQuestionId,
    defaultBusinessIndustryValue,
    onSuccess,
  } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldShowComponent, setShouldShowComponent] = useState(true);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState(''); // Store the email used for submission
  const [emailError, setEmailError] = useState<string | null>(null);
  const [businessIndustry, setBusinessIndustry] = useState(defaultBusinessIndustryValue || '');
  const [businessIndustryError, setBusinessIndustryError] = useState<string | null>(null);
  const { getDictionaryValue } = useDictionary();
  const { locale } = useRouter();
  const { update: updateSession } = useSessionSafe();
  const serverError =
    getDictionaryValue('Components.NewsletterSignup.ServerError') || 'Server error occurred';

  const loggedInuserError =
    getDictionaryValue('Components.NewsletterSignup.LoggedInError') ||
    'User must be logged in to use this feature';
  // Extract email and user ID from session if available
  const userEmail = session?.user?.email || null;
  const userId = (session?.user as { id?: string })?.id || (session as { userId?: string })?.userId;
  const isLoggedIn = !!session?.user && !!userEmail;

  useEffect(() => {
    if (!businessIndustry && defaultBusinessIndustryValue) {
      setBusinessIndustry(defaultBusinessIndustryValue);
    }
  }, [businessIndustry, defaultBusinessIndustryValue]);

  // Check subscription status and session state for all users
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      // Check if there are subscriptions to check
      if (!subscriptions || subscriptions.length === 0) {
        setShouldShowComponent(true);
        return;
      }

      // For both logged-in and anonymous users, check session storage first
      const hasCompletedSignup = subscriptions.some((subscription) =>
        NewsletterSessionManager.isSignupCompleted(subscription.SubscriptionId)
      );

      if (hasCompletedSignup) {
        setShouldShowComponent(false);
        return;
      }

      // For logged-in users, also check actual subscription status via API
      if (isLoggedIn && userId) {
        setIsCheckingSubscription(true);

        try {
          for (const subscription of subscriptions) {
            const subscriptionId = subscription.SubscriptionId;

            try {
              const response = await fetch('/api/check-subscription', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ subscriptionId, userId }),
              });

              if (response.ok) {
                const result = await response.json();
                if (result.isSubscribed) {
                  setShouldShowComponent(false);
                  setIsCheckingSubscription(false);
                  return;
                }
              } else {
                console.warn('Failed to check subscription via API:', response.statusText);
              }
            } catch (apiError) {
              console.warn('Error calling subscription check API:', apiError);
            }
          }
        } catch (error) {
          console.error('Error checking subscription status:', error);
        } finally {
          setIsCheckingSubscription(false);
        }
      }

      // If no subscriptions are active and no completed signups, show the component
      setShouldShowComponent(true);
    };

    checkSubscriptionStatus();
  }, [session, userId, subscriptions, isLoggedIn]);

  // Form handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError(null);
    }
  };

  const handleBusinessIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBusinessIndustry(e.target.value);
    if (e.target.value) {
      setBusinessIndustryError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const emailValidationMessage = emailErrorMessage || null;
      const businessIndustryValidationMessage =
        businessIndustryErrorMessage ||
        getDictionaryValue('Components.NewsletterSignup.BusinessIndustryError') ||
        'Please select a business industry';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      const isBusinessIndustryInvalid =
        !!businessIndustryRequired &&
        isUnselectedQuestionValue(businessIndustry, businessIndustryRequired);
      const isEmailInvalid = !isLoggedIn && !emailRegex.test(email);

      setBusinessIndustryError(
        isBusinessIndustryInvalid ? businessIndustryValidationMessage : null
      );
      setEmailError(isEmailInvalid ? emailValidationMessage : null);

      if (isBusinessIndustryInvalid || isEmailInvalid) {
        return;
      }

      // Include the answer whenever the dropdown is present (questionId set) and a value
      // was selected, regardless of whether the field is required.
      const questions =
        businessIndustry && businessIndustryQuestionId
          ? [
              {
                QuestionId: businessIndustryQuestionId,
                Answer: {
                  AnswerId: businessIndustry,
                },
              },
            ]
          : undefined;

      if (isLoggedIn) {
        await submitNewsletterForLoggedInUser(subscriptions, questions);
      } else {
        await submitNewsletter(email, subscriptions, questions);
      }

      if (!error && !isLoggedIn) {
        setSubmittedEmail(email); // Save the email before clearing
        setEmail('');
        setEmailError(null);
      }

      setBusinessIndustryError(null);
    } catch (err) {
      console.error('Newsletter signup failed:', err);
    }
  };

  const submitNewsletter = async (
    email?: string,
    subscriptions?: Subscriptions[],
    questions?: NewsletterQuestion[]
  ) => {
    const emailToUse = email || userEmail;

    if (!emailToUse) {
      setError(emailErrorMessage || null);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const payload = {
      email: emailToUse,
      subscriptions: subscriptions || [],
      questions,
      ...(locale && { cultureName: locale }),
    };

    try {
      const response = await fetch('/api/newsletter-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        // If the API returned structured Profile/Briteverify rule errors, resolve them
        // via the Sitecore dictionary so the user sees a friendly message.
        if (result.errors && result.errors.length > 0) {
          const messages: string[] = result.errors
            .map((errObj: { error: string }) => {
              const dictKey = PROFILE_ERROR_DICTIONARY_KEYS[errObj.error];
              return dictKey ? getDictionaryValue(dictKey) : '';
            })
            .filter(Boolean);

          const uniqueMessages = Array.from(new Set(messages));
          setError(
            uniqueMessages.length > 0
              ? uniqueMessages.join(' ')
              : getDictionaryValue(PROFILE_ERROR_DICTIONARY_KEYS['PROFILE_FAIL']) || serverError
          );
        } else {
          setError(result.error_description || result.error || serverError);
        }
        setIsSuccess(false);
      } else {
        setIsSuccess(true);
        setError(null);

        // Mark signup as completed in session for each subscription
        if (subscriptions) {
          subscriptions.forEach((subscription) => {
            NewsletterSessionManager.markSignupCompleted(subscription.SubscriptionId);
          });
          await updateSession({
            user: {
              subscriptions: subscriptions,
            },
          });
        }

        // Call analytics callback if provided
        if (onSuccess) {
          onSuccess(subscriptions);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : serverError);
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitNewsletterForLoggedInUser = async (
    subscriptions?: Subscriptions[],
    questions?: NewsletterQuestion[]
  ) => {
    if (!userEmail) {
      setError(loggedInuserError);
      return;
    }
    await submitNewsletter(userEmail, subscriptions, questions);
  };

  const reset = () => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError(null);
    setEmail('');
    setEmailError(null);
    setBusinessIndustry('');
    setBusinessIndustryError(null);
  };

  return {
    isSubmitting,
    isSuccess,
    error,
    submitNewsletter,
    submitNewsletterForLoggedInUser,
    reset,
    userEmail,
    shouldShowComponent,
    isCheckingSubscription,
    isLoggedIn,
    handleEmailChange,
    handleSubmit,
    email,
    submittedEmail,
    emailError,
    businessIndustry,
    businessIndustryError,
    handleBusinessIndustryChange,
  };
};
